import { Navigate, createBrowserRouter } from "react-router-dom";
import { RequireAuth, RequireRole } from "./auth";
import { ComplianceAppShell } from "./layouts/AppShell";
import { AdminUsersPage } from "./routes/AdminUsers";
import { AuditGlobalPage } from "./routes/AuditGlobal";
import { BatchDetailPage } from "./routes/BatchDetail";
import { BatchesPage } from "./routes/Batches";
import { ConfigPage } from "./routes/Config";
import { DataExplorerPage } from "./routes/DataExplorer";
import { ForbiddenPage } from "./routes/Forbidden";
import { IssuesGlobalPage } from "./routes/IssuesGlobal";
import { LcfsPage, LcfsTabRedirect } from "./routes/Lcfs";
import { LoginPage } from "./routes/Login";
import { NotFoundPage } from "./routes/NotFound";
import { NotificationsPage } from "./routes/Notifications";
import { ReportsGlobalPage } from "./routes/ReportsGlobal";
import { UploadDetailPage } from "./routes/UploadDetail";
import { UploadHistoryPage } from "./routes/UploadHistory";
import { UploadTemplatesPage } from "./routes/UploadTemplates";
import { UploadsPage } from "./routes/Uploads";

export const router = createBrowserRouter([
  // Public auth route — outside the AppShell so it doesn't render the sidebar.
  // There is no OAuth callback route: the API owns the code exchange and
  // redirects back to this app's origin already signed in.
  { path: "/login", element: <LoginPage /> },

  // Protected app — the AppShell is gated by RequireAuth.
  {
    path: "/",
    element: (
      <RequireAuth>
        <ComplianceAppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/batches" replace /> },
      { path: "batches", element: <BatchesPage /> },
      { path: "batches/:batchId", element: <BatchDetailPage /> },
      // Data Explorer — read-only series visualization for any role.
      { path: "data", element: <DataExplorerPage /> },
      {
        // Uploads are write actions — only operations + compliance staff.
        // Auditor is read-only (cannot upload). Real BE enforces the same
        // gate server-side via role check on POST /uploads/{type}.
        path: "uploads",
        element: (
          <RequireRole allow={["operations", "reviewer", "admin"]}>
            <UploadsPage />
          </RequireRole>
        ),
      },
      // L1-FE-03 — templates + history. These must come BEFORE
      // `uploads/:uploadId` below or "templates"/"history" would match
      // the upload-id route param.
      {
        path: "uploads/templates",
        element: (
          <RequireRole allow={["operations", "reviewer", "admin"]}>
            <UploadTemplatesPage />
          </RequireRole>
        ),
      },
      {
        path: "uploads/history",
        element: (
          <RequireRole allow={["operations", "reviewer", "admin"]}>
            <UploadHistoryPage />
          </RequireRole>
        ),
      },
      {
        // L1-FE-02 — permalink for an individual upload's parse result.
        // Same role gate as the portal: writers only; auditor doesn't see
        // uploads at all in mock mode (real BE may allow read-only access
        // — when that lands, relax this allow list).
        path: "uploads/:uploadId",
        element: (
          <RequireRole allow={["operations", "reviewer", "admin"]}>
            <UploadDetailPage />
          </RequireRole>
        ),
      },
      { path: "issues", element: <IssuesGlobalPage /> },
      {
        // LCFS reference surfaces, all four on one route as `?tab=` sections.
        // Same gate as /config: these are compliance reference views, not
        // field-ops screens, and the roster is the identity a filing is built
        // from. Reads only for now — editing the registry lands with the
        // backend endpoint.
        path: "lcfs",
        element: (
          <RequireRole allow={["reviewer", "admin"]}>
            <LcfsPage />
          </RequireRole>
        ),
      },
      // The routes those sections used to live at. Kept as redirects rather
      // than deleted: they were live, and a bookmark carries the period or
      // filter in its query string, which the redirect preserves. `lots` is a
      // distinct static segment, so ordering against the ledger route is not
      // load-bearing — but keep it first to match the uploads precedent.
      { path: "lcfs/allocation/lots", element: <LcfsTabRedirect tab="lots" /> },
      { path: "lcfs/allocation", element: <LcfsTabRedirect tab="allocation" /> },
      { path: "lcfs/fse", element: <LcfsTabRedirect tab="fse" /> },
      { path: "lcfs/pathways", element: <LcfsTabRedirect tab="dairies" /> },
      { path: "reports", element: <ReportsGlobalPage /> },
      // Notification preferences + dispatch log — self-service for any role.
      { path: "notifications", element: <NotificationsPage /> },
      {
        // Audit timeline is for auditors + compliance staff. Operations
        // role is excluded (the field crew doesn't need the audit log).
        path: "audit",
        element: (
          <RequireRole allow={["reviewer", "admin"]}>
            <AuditGlobalPage />
          </RequireRole>
        ),
      },
      {
        // Configuration — customer profile versions. Reads are open to any
        // compliance role on the BE; creating a version is admin-only (gated
        // in-page). We surface it to compliance staff + auditors (not the
        // field-ops crew, who don't need reference config).
        path: "config",
        element: (
          <RequireRole allow={["reviewer", "admin"]}>
            <ConfigPage />
          </RequireRole>
        ),
      },
      {
        // User-role management — admin-only. PATCH /users/{id}/role is
        // gated by FORBIDDEN server-side; we mirror that in the UI.
        path: "admin/users",
        element: (
          <RequireRole allow={["admin"]}>
            <AdminUsersPage />
          </RequireRole>
        ),
      },
      { path: "403", element: <ForbiddenPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
